import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { ArrowLeft, Book, Moon, ChevronDown, ChevronUp, Sparkles, Star, Eye, Shuffle, Heart, Shield, Sun, Flame, Droplets, Wind, Mountain, Brain, History, Crown, Lock, ArrowRight, Loader2 } from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Les 22 Arcanes Majeurs - Résumé
const MAJOR_ARCANA_SUMMARY = [
  { number: 0, name: "Le Mat", keywords: "Commencement, liberté, innocence", element: "Air" },
  { number: 1, name: "Le Bateleur", keywords: "Action, initiative, potentiel", element: "Feu" },
  { number: 2, name: "La Papesse", keywords: "Intuition, mystère, sagesse cachée", element: "Eau" },
  { number: 3, name: "L'Impératrice", keywords: "Fertilité, créativité, abondance", element: "Terre" },
  { number: 4, name: "L'Empereur", keywords: "Autorité, structure, stabilité", element: "Feu" },
  { number: 5, name: "Le Pape", keywords: "Spiritualité, tradition, enseignement", element: "Terre" },
  { number: 6, name: "L'Amoureux", keywords: "Choix, union, dilemme", element: "Air" },
  { number: 7, name: "Le Chariot", keywords: "Victoire, maîtrise, avancement", element: "Eau" },
  { number: 8, name: "La Justice", keywords: "Équilibre, vérité, conséquences", element: "Air" },
  { number: 9, name: "L'Ermite", keywords: "Introspection, sagesse, solitude", element: "Terre" },
  { number: 10, name: "La Roue de Fortune", keywords: "Cycles, changement, destin", element: "Feu" },
  { number: 11, name: "La Force", keywords: "Courage, patience, maîtrise", element: "Feu" },
  { number: 12, name: "Le Pendu", keywords: "Suspension, sacrifice, perspective", element: "Eau" },
  { number: 13, name: "L'Arcane sans Nom", keywords: "Transformation, fin, renouveau", element: "Eau" },
  { number: 14, name: "Tempérance", keywords: "Équilibre, harmonie, patience", element: "Eau" },
  { number: 15, name: "Le Diable", keywords: "Attachement, passion, tentation", element: "Terre" },
  { number: 16, name: "La Maison Dieu", keywords: "Destruction, révélation, libération", element: "Feu" },
  { number: 17, name: "L'Étoile", keywords: "Espoir, inspiration, sérénité", element: "Air" },
  { number: 18, name: "La Lune", keywords: "Inconscient, illusion, intuition", element: "Eau" },
  { number: 19, name: "Le Soleil", keywords: "Joie, succès, clarté", element: "Feu" },
  { number: 20, name: "Le Jugement", keywords: "Renaissance, appel, libération", element: "Feu" },
  { number: 21, name: "Le Monde", keywords: "Accomplissement, unité, réalisation", element: "Terre" }
];

// Types de tirages
const SPREAD_TYPES = [
  {
    name: "Tirage 3 Cartes",
    cards: 3,
    description: "Passé - Présent - Futur. Idéal pour une question simple ou une vue d'ensemble rapide.",
    positions: ["Le passé (ce qui a mené à la situation)", "Le présent (la situation actuelle)", "Le futur (l'évolution probable)"]
  },
  {
    name: "Tirage Situation",
    cards: 3,
    description: "Situation - Défi - Conseil. Pour comprendre un problème et trouver des solutions.",
    positions: ["La situation (ce qui se passe)", "Le défi (l'obstacle principal)", "Le conseil (la guidance)"]
  },
  {
    name: "Croix Celtique",
    cards: 10,
    description: "Le tirage le plus complet pour une analyse approfondie d'une situation complexe.",
    positions: ["Position actuelle", "Défi immédiat", "Fondement", "Passé récent", "Couronnement", "Futur proche", "Attitude", "Environnement", "Espoirs/Craintes", "Résultat"]
  },
  {
    name: "Oui ou Non",
    cards: 1,
    description: "Une seule carte pour une réponse directe. Simple mais efficace.",
    positions: ["La réponse (interprétée selon l'énergie de la carte)"]
  },
  {
    name: "Tirage Amour",
    cards: 5,
    description: "Pour explorer les dynamiques relationnelles et émotionnelles.",
    positions: ["Vous", "L'autre / la relation", "Ce qui vous unit", "Ce qui vous sépare", "Évolution potentielle"]
  }
];

const ModuleCard = ({ title, icon: Icon, color, children, defaultOpen = false }) => {
  const [isOpen, setIsOpen] = useState(defaultOpen);
  
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      className="bg-white rounded-3xl border border-purple-200 shadow-lg overflow-hidden"
    >
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={`w-full p-6 flex items-center justify-between ${color} transition-colors`}
      >
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center">
            <Icon className="h-6 w-6 text-white" />
          </div>
          <h2 className="text-xl md:text-2xl text-white text-left font-bold">{title}</h2>
        </div>
        {isOpen ? (
          <ChevronUp className="h-6 w-6 text-white" />
        ) : (
          <ChevronDown className="h-6 w-6 text-white" />
        )}
      </button>
      
      {isOpen && (
        <motion.div
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: "auto" }}
          exit={{ opacity: 0, height: 0 }}
          className="p-6"
        >
          {children}
        </motion.div>
      )}
    </motion.div>
  );
};

const getElementIcon = (element) => {
  switch(element) {
    case 'Feu': return <Flame className="h-4 w-4 text-red-500" />;
    case 'Eau': return <Droplets className="h-4 w-4 text-blue-500" />;
    case 'Air': return <Wind className="h-4 w-4 text-sky-500" />;
    case 'Terre': return <Mountain className="h-4 w-4 text-amber-600" />;
    default: return <Star className="h-4 w-4 text-purple-500" />;
  }
};

const MysteriaeKnowledgePage = () => {
  const navigate = useNavigate();
  const [hasPremiumAccess, setHasPremiumAccess] = useState(false);
  const [checkingAccess, setCheckingAccess] = useState(true);

  useEffect(() => {
    const checkAccess = async () => {
      try {
        const response = await axios.get(`${API_URL}/api/premium/check/mysteriae`, {
          withCredentials: true
        });
        setHasPremiumAccess(response.data.has_access === true);
      } catch (error) {
        setHasPremiumAccess(false);
      } finally {
        setCheckingAccess(false);
      }
    };
    checkAccess();
  }, []);

  // Loading state
  if (checkingAccess) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-violet-50 via-white to-purple-50 flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-purple-500" />
      </div>
    );
  }

  // Premium upsell
  if (!hasPremiumAccess) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-violet-50 via-white to-purple-50 py-12 px-4">
        <div className="max-w-5xl mx-auto">
          <Button
            variant="ghost"
            onClick={() => navigate('/mysteriae')}
            className="mb-6 rounded-full text-gray-600 hover:bg-purple-50"
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            Retour
          </Button>
          
          <div className="text-center mb-8">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-purple-100 rounded-full border border-purple-200 mb-4">
              <Lock className="h-4 w-4 text-purple-600" />
              <span className="text-purple-700 text-sm font-medium">Premium</span>
            </div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">Découvrir le Tarot</h1>
            <p className="text-gray-600">Accédez à la base de connaissances complète</p>
          </div>
          
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="max-w-md mx-auto"
          >
            <div className="bg-gradient-to-br from-violet-50 to-purple-50 rounded-3xl border-2 border-purple-200 p-8 shadow-xl">
              <div className="flex justify-center mb-6">
                <div className="w-20 h-20 bg-gradient-to-br from-purple-500 to-indigo-600 rounded-full flex items-center justify-center shadow-lg">
                  <Crown className="h-10 w-10 text-white" />
                </div>
              </div>
              
              <div className="space-y-3 mb-8">
                {[
                  "Les 22 Arcanes Majeurs",
                  "Les 56 Arcanes Mineurs",
                  "Les types de tirages",
                  "Histoire et symbolisme",
                  "Conseils d'interprétation"
                ].map((benefit, i) => (
                  <div key={i} className="flex items-center gap-3 p-2 bg-white/60 rounded-lg">
                    <Sparkles className="h-4 w-4 text-purple-500 flex-shrink-0" />
                    <span className="text-sm text-gray-700">{benefit}</span>
                  </div>
                ))}
              </div>
              
              <Button
                onClick={() => navigate('/premium')}
                className="w-full bg-gradient-to-r from-purple-500 to-indigo-600 hover:from-purple-600 hover:to-indigo-700 text-white px-8 py-3 rounded-xl font-semibold shadow-lg"
                data-testid="upgrade-premium-btn"
              >
                <Crown className="h-5 w-5 mr-2" />
                Devenir Premium
                <ArrowRight className="h-5 w-5 ml-2" />
              </Button>
              
              <p className="text-xs text-gray-500 mt-4 text-center">
                À partir de 1.99€ pour 7 jours d'essai
              </p>
            </div>
          </motion.div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-violet-50 via-white to-purple-50 py-12 px-4 md:px-8">
      <div className="max-w-5xl mx-auto">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-12"
        >
          <Button
            data-testid="back-button"
            variant="ghost"
            onClick={() => navigate('/mysteriae')}
            className="mb-6 rounded-full text-gray-600 hover:bg-purple-50"
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            Retour
          </Button>
          
          <div className="bg-gradient-to-br from-purple-500 to-indigo-600 p-8 md:p-10 rounded-3xl shadow-xl mb-8">
            <div className="flex flex-col md:flex-row items-start gap-6">
              <div className="w-20 h-20 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center flex-shrink-0">
                <Book className="h-10 w-10 text-white" />
              </div>
              <div>
                <h1 className="text-3xl md:text-4xl lg:text-5xl mb-3 text-white font-bold">
                  Découvrir le Tarot
                </h1>
                <p className="text-white/90 text-base md:text-lg mb-4">
                  L'art ancestral de la lecture des Arcanes
                </p>
                <p className="text-white/80 text-sm max-w-2xl">
                  Le Tarot est un outil d'introspection et de développement personnel, pas de prédiction. 
                  Les cartes sont des miroirs qui reflètent nos questionnements intérieurs.
                </p>
              </div>
            </div>
          </div>

          {/* Quick Navigation */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-8">
            {[
              { icon: Moon, label: "Les Arcanes", color: "bg-purple-50 text-purple-700 border-purple-200" },
              { icon: Shuffle, label: "Tirages", color: "bg-indigo-50 text-indigo-700 border-indigo-200" },
              { icon: Eye, label: "Symbolisme", color: "bg-violet-50 text-violet-700 border-violet-200" },
              { icon: History, label: "Histoire", color: "bg-amber-50 text-amber-700 border-amber-200" }
            ].map((item, i) => (
              <div key={i} className={`${item.color} p-3 rounded-xl text-center border`}>
                <item.icon className="h-5 w-5 mx-auto mb-1" />
                <span className="text-xs font-medium">{item.label}</span>
              </div>
            ))}
          </div>
        </motion.div>

        {/* Modules */}
        <div className="space-y-6">
          
          {/* Module 1: Introduction */}
          <ModuleCard 
            title="Introduction au Tarot" 
            icon={Sparkles}
            color="bg-gradient-to-r from-purple-500 to-purple-600"
            defaultOpen={true}
          >
            <div className="space-y-6">
              <div className="bg-purple-50 p-5 rounded-xl border border-purple-200">
                <h3 className="text-lg font-bold text-gray-800 mb-3">Qu'est-ce que le Tarot ?</h3>
                <p className="text-gray-600 text-sm leading-relaxed">
                  Le Tarot est un jeu de cartes composé de 78 lames, dont 22 Arcanes Majeurs et 56 Arcanes Mineurs. 
                  Originaire d'Italie au XVe siècle, il a évolué pour devenir un puissant outil d'introspection et de développement personnel. 
                  Le Tarot de Marseille, popularisé au XVIIIe siècle, est la version la plus répandue en France.
                </p>
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                <div className="bg-gray-50 p-4 rounded-xl border border-gray-200">
                  <h4 className="font-semibold text-purple-700 mb-2">Les Arcanes Majeurs</h4>
                  <ul className="text-sm text-gray-600 space-y-1">
                    <li>• <strong>22 cartes</strong> numérotées de 0 à 21</li>
                    <li>• Représentent les grandes étapes de la vie</li>
                    <li>• Symbolisent le voyage archétypal de l'âme</li>
                    <li>• Du Mat (0) au Monde (21)</li>
                  </ul>
                </div>
                <div className="bg-gray-50 p-4 rounded-xl border border-gray-200">
                  <h4 className="font-semibold text-purple-700 mb-2">Les Arcanes Mineurs</h4>
                  <ul className="text-sm text-gray-600 space-y-1">
                    <li>• <strong>56 cartes</strong> en 4 suites</li>
                    <li>• Bâtons, Coupes, Épées, Deniers</li>
                    <li>• Représentent la vie quotidienne</li>
                    <li>• Nuances et détails des situations</li>
                  </ul>
                </div>
              </div>

              <div className="bg-amber-50 p-4 rounded-xl border border-amber-200">
                <h4 className="font-semibold text-amber-700 mb-2">Important</h4>
                <p className="text-amber-600 text-sm">
                  Le Tarot ne prédit pas l'avenir de manière absolue. Il offre des perspectives, des pistes de réflexion 
                  et des éclairages sur notre situation présente. L'interprétation dépend du contexte et du libre arbitre.
                </p>
              </div>
            </div>
          </ModuleCard>

          {/* Module 2: Les 22 Arcanes Majeurs */}
          <ModuleCard 
            title="Les 22 Arcanes Majeurs" 
            icon={Moon}
            color="bg-gradient-to-r from-indigo-500 to-indigo-600"
          >
            <p className="text-gray-600 mb-6 text-sm">
              Les 22 Arcanes Majeurs forment le cœur symbolique du Tarot. Chaque carte représente une étape 
              du voyage initiatique, du Mat innocent au Monde accompli.
            </p>
            <div className="grid md:grid-cols-2 gap-3">
              {MAJOR_ARCANA_SUMMARY.map((card) => (
                <div 
                  key={card.number} 
                  className="bg-gray-50 p-3 rounded-xl border border-gray-200 flex items-center gap-3 cursor-pointer hover:bg-purple-50 hover:border-purple-300 transition-all"
                  onClick={() => navigate(`/mysteriae/arcanes/${card.number}`)}
                >
                  <span className="w-10 h-10 bg-gradient-to-br from-purple-500 to-indigo-600 rounded-lg flex items-center justify-center text-white font-bold text-sm">
                    {card.number}
                  </span>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <h4 className="font-semibold text-gray-800 text-sm truncate">{card.name}</h4>
                      {getElementIcon(card.element)}
                    </div>
                    <p className="text-xs text-gray-500 truncate">{card.keywords}</p>
                  </div>
                </div>
              ))}
            </div>
            <div className="mt-6 text-center">
              <Button
                onClick={() => navigate('/mysteriae/arcanes')}
                className="rounded-full bg-gradient-to-r from-purple-500 to-indigo-600 hover:from-purple-400 hover:to-indigo-500"
              >
                <Eye className="mr-2 h-4 w-4" />
                Voir toutes les cartes en détail
              </Button>
            </div>
          </ModuleCard>

          {/* Module 3: Les Éléments */}
          <ModuleCard 
            title="Les Quatre Éléments" 
            icon={Flame}
            color="bg-gradient-to-r from-orange-500 to-red-500"
          >
            <p className="text-gray-600 mb-6 text-sm">
              Chaque Arcane est associé à un élément, apportant une couleur énergétique à son interprétation.
            </p>
            <div className="grid md:grid-cols-2 gap-4">
              <div className="bg-red-50 p-4 rounded-xl border border-red-200">
                <div className="flex items-center gap-3 mb-3">
                  <Flame className="h-8 w-8 text-red-500" />
                  <h4 className="font-bold text-gray-800">Feu</h4>
                </div>
                <p className="text-sm text-gray-600 mb-2">Énergie, action, volonté, passion, créativité</p>
                <p className="text-xs text-red-600">Cartes: Le Bateleur, L'Empereur, La Roue de Fortune, La Force, La Maison Dieu, Le Soleil, Le Jugement</p>
              </div>
              <div className="bg-blue-50 p-4 rounded-xl border border-blue-200">
                <div className="flex items-center gap-3 mb-3">
                  <Droplets className="h-8 w-8 text-blue-500" />
                  <h4 className="font-bold text-gray-800">Eau</h4>
                </div>
                <p className="text-sm text-gray-600 mb-2">Émotions, intuition, inconscient, réceptivité</p>
                <p className="text-xs text-blue-600">Cartes: La Papesse, Le Chariot, Le Pendu, L'Arcane sans Nom, Tempérance, La Lune</p>
              </div>
              <div className="bg-sky-50 p-4 rounded-xl border border-sky-200">
                <div className="flex items-center gap-3 mb-3">
                  <Wind className="h-8 w-8 text-sky-500" />
                  <h4 className="font-bold text-gray-800">Air</h4>
                </div>
                <p className="text-sm text-gray-600 mb-2">Mental, communication, intellect, objectivité</p>
                <p className="text-xs text-sky-600">Cartes: Le Mat, L'Amoureux, La Justice, L'Étoile</p>
              </div>
              <div className="bg-amber-50 p-4 rounded-xl border border-amber-200">
                <div className="flex items-center gap-3 mb-3">
                  <Mountain className="h-8 w-8 text-amber-600" />
                  <h4 className="font-bold text-gray-800">Terre</h4>
                </div>
                <p className="text-sm text-gray-600 mb-2">Matière, stabilité, concret, réalisation</p>
                <p className="text-xs text-amber-600">Cartes: L'Impératrice, Le Pape, L'Ermite, Le Diable, Le Monde</p>
              </div>
            </div>
          </ModuleCard>

          {/* Module 4: Les Types de Tirages */}
          <ModuleCard 
            title="Les Types de Tirages" 
            icon={Shuffle}
            color="bg-gradient-to-r from-violet-500 to-violet-600"
          >
            <p className="text-gray-600 mb-6 text-sm">
              Chaque tirage a sa spécificité. Choisissez celui qui correspond le mieux à votre question.
            </p>
            <div className="space-y-4">
              {SPREAD_TYPES.map((spread, index) => (
                <div key={index} className="bg-gray-50 p-4 rounded-xl border border-gray-200">
                  <div className="flex items-center justify-between mb-2">
                    <h4 className="font-bold text-gray-800">{spread.name}</h4>
                    <span className="px-2 py-1 bg-purple-100 text-purple-700 text-xs rounded-full font-medium">
                      {spread.cards} carte{spread.cards > 1 ? 's' : ''}
                    </span>
                  </div>
                  <p className="text-sm text-gray-600 mb-3">{spread.description}</p>
                  <div className="flex flex-wrap gap-2">
                    {spread.positions.map((pos, i) => (
                      <span key={i} className="text-xs bg-white border border-purple-200 px-2 py-1 rounded-full text-purple-700">
                        {i + 1}. {pos}
                      </span>
                    ))}
                  </div>
                </div>
              ))}
            </div>
            <div className="mt-6 text-center">
              <Button
                onClick={() => navigate('/mysteriae/draw')}
                className="rounded-full bg-gradient-to-r from-violet-500 to-purple-600 hover:from-violet-400 hover:to-purple-500"
              >
                <Sparkles className="mr-2 h-4 w-4" />
                Faire un tirage
              </Button>
            </div>
          </ModuleCard>

          {/* Module 5: Cartes Renversées */}
          <ModuleCard 
            title="Les Cartes Renversées" 
            icon={Shield}
            color="bg-gradient-to-r from-slate-500 to-slate-600"
          >
            <div className="space-y-4">
              <div className="bg-slate-50 p-4 rounded-xl border border-slate-200">
                <h4 className="font-semibold text-gray-800 mb-2">Qu'est-ce qu'une carte renversée ?</h4>
                <p className="text-sm text-gray-600">
                  Une carte apparaît "renversée" quand elle sort tête en bas lors du tirage. 
                  Son interprétation peut alors être nuancée, sans être forcément négative.
                </p>
              </div>
              
              <div className="grid md:grid-cols-3 gap-4">
                <div className="bg-amber-50 p-4 rounded-xl border border-amber-200">
                  <h5 className="font-semibold text-amber-700 mb-2">Énergie bloquée</h5>
                  <p className="text-xs text-gray-600">L'énergie de la carte est présente mais ne s'exprime pas pleinement.</p>
                </div>
                <div className="bg-blue-50 p-4 rounded-xl border border-blue-200">
                  <h5 className="font-semibold text-blue-700 mb-2">Intériorisation</h5>
                  <p className="text-xs text-gray-600">L'énergie se manifeste de manière interne plutôt qu'externe.</p>
                </div>
                <div className="bg-purple-50 p-4 rounded-xl border border-purple-200">
                  <h5 className="font-semibold text-purple-700 mb-2">En développement</h5>
                  <p className="text-xs text-gray-600">La qualité est en cours d'apprentissage ou nécessite attention.</p>
                </div>
              </div>

              <div className="bg-emerald-50 p-4 rounded-xl border border-emerald-200">
                <p className="text-sm text-emerald-700">
                  <strong>Note :</strong> Certains tarologues n'utilisent pas les cartes renversées. C'est une question de préférence personnelle.
                </p>
              </div>
            </div>
          </ModuleCard>

          {/* Module 6: Histoire */}
          <ModuleCard 
            title="Histoire du Tarot" 
            icon={History}
            color="bg-gradient-to-r from-amber-500 to-amber-600"
          >
            <div className="space-y-4">
              <div className="grid md:grid-cols-2 gap-4">
                <div className="bg-amber-50 p-4 rounded-xl border border-amber-200">
                  <h4 className="font-semibold text-gray-800 mb-2">XVe siècle - Origines</h4>
                  <p className="text-sm text-gray-600">
                    Les premiers tarots documentés apparaissent en Italie du Nord, notamment le Tarot Visconti-Sforza. 
                    Ils étaient utilisés comme jeu de cartes par la noblesse.
                  </p>
                </div>
                <div className="bg-orange-50 p-4 rounded-xl border border-orange-200">
                  <h4 className="font-semibold text-gray-800 mb-2">XVIIIe siècle - Marseille</h4>
                  <p className="text-sm text-gray-600">
                    Les cartiers marseillais standardisent le design qui deviendra le "Tarot de Marseille". 
                    C'est à cette époque que le Tarot commence à être utilisé pour la divination.
                  </p>
                </div>
                <div className="bg-yellow-50 p-4 rounded-xl border border-yellow-200">
                  <h4 className="font-semibold text-gray-800 mb-2">XIXe siècle - Ésotérisme</h4>
                  <p className="text-sm text-gray-600">
                    Éliphas Lévi et l'ordre de la Golden Dawn intègrent le Tarot dans leurs systèmes ésotériques, 
                    le liant à la Kabbale et l'astrologie.
                  </p>
                </div>
                <div className="bg-lime-50 p-4 rounded-xl border border-lime-200">
                  <h4 className="font-semibold text-gray-800 mb-2">XXe-XXIe siècles</h4>
                  <p className="text-sm text-gray-600">
                    Le Tarot se démocratise comme outil de développement personnel. 
                    Des centaines de versions artistiques voient le jour.
                  </p>
                </div>
              </div>
            </div>
          </ModuleCard>

          {/* Module 7: Conseils de lecture */}
          <ModuleCard 
            title="Conseils pour bien lire" 
            icon={Heart}
            color="bg-gradient-to-r from-pink-500 to-rose-500"
          >
            <div className="space-y-4">
              <div className="grid md:grid-cols-2 gap-4">
                <div className="bg-pink-50 p-4 rounded-xl border border-pink-200">
                  <h4 className="font-semibold text-pink-700 mb-2">Avant le tirage</h4>
                  <ul className="text-sm text-gray-600 space-y-1">
                    <li>• Formulez clairement votre question</li>
                    <li>• Prenez un moment de calme</li>
                    <li>• Évitez les questions "oui/non" trop fermées</li>
                    <li>• Soyez ouvert aux réponses inattendues</li>
                  </ul>
                </div>
                <div className="bg-rose-50 p-4 rounded-xl border border-rose-200">
                  <h4 className="font-semibold text-rose-700 mb-2">Pendant la lecture</h4>
                  <ul className="text-sm text-gray-600 space-y-1">
                    <li>• Observez d'abord l'ensemble du tirage</li>
                    <li>• Notez les symboles qui vous marquent</li>
                    <li>• Faites confiance à votre intuition</li>
                    <li>• Cherchez les liens entre les cartes</li>
                  </ul>
                </div>
              </div>

              <div className="bg-purple-50 p-4 rounded-xl border border-purple-200">
                <h4 className="font-semibold text-purple-700 mb-2">Questions à éviter</h4>
                <div className="grid md:grid-cols-2 gap-2 text-sm">
                  <div className="text-gray-600">
                    <span className="text-red-500 mr-2">✗</span>
                    "Vais-je gagner au loto ?"
                  </div>
                  <div className="text-gray-600">
                    <span className="text-emerald-500 mr-2">✓</span>
                    "Comment améliorer ma situation financière ?"
                  </div>
                  <div className="text-gray-600">
                    <span className="text-red-500 mr-2">✗</span>
                    "Quand vais-je rencontrer l'amour ?"
                  </div>
                  <div className="text-gray-600">
                    <span className="text-emerald-500 mr-2">✓</span>
                    "Que dois-je développer pour attirer l'amour ?"
                  </div>
                </div>
              </div>
            </div>
          </ModuleCard>

        </div>

        {/* CTA */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="mt-12 text-center"
        >
          <p className="text-gray-600 mb-6">
            Prêt à explorer les Arcanes ?
          </p>
          <div className="flex flex-wrap gap-4 justify-center">
            <Button
              data-testid="start-draw-btn"
              onClick={() => navigate('/mysteriae/draw')}
              size="lg"
              className="rounded-full bg-gradient-to-r from-purple-500 to-indigo-600 hover:from-purple-400 hover:to-indigo-500 text-white px-8 py-6 text-base font-semibold shadow-lg"
            >
              <Sparkles className="mr-2 h-5 w-5" />
              Faire un tirage
            </Button>
            <Button
              onClick={() => navigate('/mysteriae/quiz')}
              size="lg"
              variant="outline"
              className="rounded-full border-purple-300 text-purple-700 hover:bg-purple-50 px-8 py-6 text-base font-semibold"
            >
              <Brain className="mr-2 h-5 w-5" />
              Tester mes connaissances
            </Button>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default MysteriaeKnowledgePage;
